import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ADMIN_PERMISSION_GROUPS, type AdminPermission } from '@tucupet/shared';
import { PageHeader, Card, Btn, Modal, Field, Select } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

interface PublicUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'client' | 'operator' | 'admin';
  verified: boolean;
  banned?: boolean;
  walletBalance: number;
  walletStatus?: 'active' | 'frozen' | 'closed';
  initials?: string;
  createdAt?: string;
  lastLoginAt?: string;
  totpEnabled?: boolean;
  permissions?: string[];
}

interface WalletStatePayload {
  state: {
    balance: number;
    held: number;
    available: number;
    status: string;
    recent: Array<{ id: string; kind: string; amount: number; balanceAfter: number; description: string; reference: string; createdAt: string }>;
  };
  holds: Array<{ _id: string; amount: number; reference: string; status: string; expiresAt: string; createdAt: string; resolvedAt?: string | null }>;
}

interface OrderSum {
  id: string;
  code: string;
  status: string;
  fuelVariantName: string;
  fuelOctane: string;
  liters: number;
  total: number;
  stationName: string;
  slotDate: string;
  slotTime: string;
  slotPump: string;
  method?: string | null;
  createdAt: string;
}

const KIND_LABEL: Record<string, string> = {
  top_up: 'Recarga',
  debit: 'Pago de orden',
  refund: 'Reembolso',
  adjustment: 'Ajuste manual',
  hold: 'Reserva',
  hold_release: 'Liberación de reserva',
  hold_settle: 'Reserva → débito',
};

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { me } = useAuth();
  const [adjusting, setAdjusting] = useState(false);
  const [editing, setEditing] = useState(false);

  const { data: user, isLoading: loadingUser } = useQuery<PublicUser>({
    queryKey: ['admin', 'user', id],
    queryFn: () => api(`/api/admin/users/${id}`),
    enabled: !!id,
  });

  const { data: wallet } = useQuery<WalletStatePayload>({
    queryKey: ['admin', 'user-wallet', id],
    queryFn: () => api(`/api/admin/users/${id}/wallet/state`),
    enabled: !!id,
  });

  const { data: orders } = useQuery<{ items: OrderSum[]; total: number }>({
    queryKey: ['admin', 'user-orders', id],
    queryFn: () => api('/api/admin/orders', { query: { userId: id, limit: 20 } }),
    enabled: !!id,
  });

  const freeze = useMutation({
    mutationFn: () => api(`/api/admin/users/${id}/wallet/freeze`, { body: {} }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'user', id] });
      qc.invalidateQueries({ queryKey: ['admin', 'user-wallet', id] });
    },
  });
  const unfreeze = useMutation({
    mutationFn: () => api(`/api/admin/users/${id}/wallet/unfreeze`, { body: {} }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'user', id] });
      qc.invalidateQueries({ queryKey: ['admin', 'user-wallet', id] });
    },
  });

  if (loadingUser || !user) return <div style={{ padding: 40, color: 'var(--text-3)' }}>Cargando…</div>;

  const activeHolds = wallet?.holds.filter((h) => h.status === 'active') ?? [];
  const isFrozen = user.walletStatus === 'frozen';

  return (
    <div>
      <Link to="/users" style={{ color: 'var(--text-2)', fontSize: 13, textDecoration: 'none' }}>← Usuarios</Link>
      <PageHeader
        kicker={`USUARIO · ${user.role.toUpperCase()}`}
        title={`${user.firstName} ${user.lastName}`}
        sub={`${user.email}${user.banned ? ' · BANEADO' : user.verified ? '' : ' · sin verificar'}`}
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn size="sm" variant="ghost" onClick={() => setEditing(true)}>Editar</Btn>
            <Btn size="sm" variant="ghost" onClick={() => setAdjusting(true)}>Ajustar saldo</Btn>
            {isFrozen ? (
              <Btn size="sm" variant="ghost" onClick={() => unfreeze.mutate()} disabled={unfreeze.isPending}>Descongelar</Btn>
            ) : (
              <Btn size="sm" variant="danger" onClick={() => { if (confirm(`Congelar wallet de ${user.email}?`)) freeze.mutate(); }} disabled={freeze.isPending}>Congelar</Btn>
            )}
          </div>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginTop: 16, marginBottom: 20 }}>
        <Stat label="SALDO" value={`$${(wallet?.state.balance ?? user.walletBalance).toFixed(2)}`} accent />
        <Stat label="DISPONIBLE" value={`$${(wallet?.state.available ?? user.walletBalance).toFixed(2)}`} />
        <Stat label="RETENIDO" value={`$${(wallet?.state.held ?? 0).toFixed(2)}`} />
        <Stat label="ESTADO" value={user.walletStatus?.toUpperCase() ?? 'ACTIVE'} danger={isFrozen} />
        <Stat label="2FA" value={user.totpEnabled ? '✓ Activo' : '—'} />
      </div>

      {activeHolds.length > 0 && (
        <Card style={{ marginBottom: 20, borderColor: 'color-mix(in oklab, oklch(0.78 0.16 70) 50%, var(--border))' }}>
          <div className="mono" style={{ fontSize: 10, color: 'oklch(0.78 0.16 70)', letterSpacing: '0.14em' }}>HOLDS ACTIVOS · {activeHolds.length}</div>
          <table style={{ width: '100%', marginTop: 12, fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={th}>Ref</th>
                <th style={th}>Monto</th>
                <th style={th}>Expira</th>
              </tr>
            </thead>
            <tbody>
              {activeHolds.map((h) => (
                <tr key={h._id}>
                  <td style={td}><span className="mono">{h.reference}</span></td>
                  <td style={td}><span className="mono" style={{ color: 'oklch(0.78 0.16 70)', fontWeight: 600 }}>${h.amount.toFixed(2)}</span></td>
                  <td style={{ ...td, fontSize: 11, color: 'var(--text-3)' }}>{new Date(h.expiresAt).toLocaleString('es-MX')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16 }}>
        <div>
          <div className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', color: 'var(--text-3)', marginBottom: 8 }}>MOVIMIENTOS RECIENTES</div>
          <Card>
            {wallet?.state.recent.length === 0 ? (
              <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '8px 0' }}>Sin movimientos.</div>
            ) : (
              <table style={{ width: '100%', fontSize: 12 }}>
                <tbody>
                  {wallet?.state.recent.map((t) => (
                    <tr key={t.id} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={td}>
                        <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)' }}>{KIND_LABEL[t.kind] ?? t.kind}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>{t.description}</div>
                        <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>{new Date(t.createdAt).toLocaleString('es-MX')}</div>
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <div className="mono" style={{ fontSize: 13, fontWeight: 600, color: t.amount > 0 ? 'var(--accent)' : t.amount < 0 ? 'var(--text)' : 'var(--text-3)' }}>
                          {t.amount > 0 ? '+' : ''}${t.amount.toFixed(2)}
                        </div>
                        <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>bal ${t.balanceAfter.toFixed(2)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>

        <div>
          <div className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', color: 'var(--text-3)', marginBottom: 8 }}>
            ÓRDENES ({orders?.total ?? 0})
          </div>
          <Card>
            {orders?.items.length === 0 ? (
              <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '8px 0' }}>Sin órdenes.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {orders?.items.map((o) => (
                  <Link key={o.id} to={`/orders/${o.id}`} style={{ display: 'block', padding: '10px 0', borderTop: '1px solid var(--border)', textDecoration: 'none', color: 'inherit' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                          <span className="mono" style={{ fontSize: 12, fontWeight: 600 }}>{o.code}</span>
                          <span className="mono" style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{o.status}</span>
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>{o.fuelVariantName} {o.fuelOctane} · {o.liters} L · {o.stationName}</div>
                        <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>{o.slotDate} {o.slotTime} · Bomba {o.slotPump}</div>
                      </div>
                      <div className="mono" style={{ fontSize: 13, fontWeight: 600 }}>${o.total.toFixed(2)}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {me?.role === 'admin' && user.role !== 'admin' && (
        <PermissionsCard user={user} onSaved={() => qc.invalidateQueries({ queryKey: ['admin', 'user', id] })} />
      )}

      <EditModal open={editing} user={user} onClose={() => setEditing(false)} onSaved={() => qc.invalidateQueries({ queryKey: ['admin', 'user', id] })} />
      <AdjustModal open={adjusting} user={user} onClose={() => setAdjusting(false)} onSaved={() => {
        qc.invalidateQueries({ queryKey: ['admin', 'user', id] });
        qc.invalidateQueries({ queryKey: ['admin', 'user-wallet', id] });
      }} />
    </div>
  );
}

function PermissionsCard({ user, onSaved }: { user: PublicUser; onSaved: () => void }) {
  const [draft, setDraft] = useState<Set<string>>(new Set(user.permissions ?? []));
  useEffect(() => { setDraft(new Set(user.permissions ?? [])); }, [user.permissions]);
  const dirty = useMemo(() => {
    const cur = user.permissions ?? [];
    if (cur.length !== draft.size) return true;
    return !cur.every((p) => draft.has(p));
  }, [user.permissions, draft]);

  const save = useMutation({
    mutationFn: () => api(`/api/admin/users/${user.id}/permissions`, { method: 'PATCH', body: { permissions: Array.from(draft) } }),
    onSuccess: () => onSaved(),
  });

  const toggle = (perm: string) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(perm)) next.delete(perm); else next.add(perm);
      return next;
    });
  };

  return (
    <Card style={{ marginTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.14em' }}>PERMISOS DE BACKOFFICE</div>
          <div style={{ marginTop: 6, fontSize: 13, color: 'var(--text-2)' }}>
            {user.role === 'operator' ? 'Operator: configura aquí qué entidades puede ver y editar.' : 'Cliente: normalmente no necesita permisos de admin. Si los marcas, también necesita rol operator/admin.'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn size="sm" variant="ghost" onClick={() => setDraft(new Set())} disabled={draft.size === 0 || save.isPending}>Quitar todos</Btn>
          <Btn size="sm" onClick={() => save.mutate()} disabled={!dirty || save.isPending}>{save.isPending ? '…' : 'Guardar'}</Btn>
        </div>
      </div>
      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
        {ADMIN_PERMISSION_GROUPS.map((g) => (
          <div key={g.entity} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 12, padding: 14 }}>
            <div className="mono" style={{ fontSize: 10, letterSpacing: '0.12em', color: 'var(--text-3)' }}>{g.label.toUpperCase()}</div>
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {g.perms.map((p) => (
                <label key={p} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={draft.has(p)} onChange={() => toggle(p)} />
                  <span className="mono" style={{ color: 'var(--text-2)' }}>{p}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

const th: React.CSSProperties = { textAlign: 'left', padding: '6px 0', fontSize: 10, letterSpacing: '0.1em', color: 'var(--text-3)', textTransform: 'uppercase' };
const td: React.CSSProperties = { padding: '6px 0', verticalAlign: 'top' };

function Stat({ label, value, accent, danger }: { label: string; value: string; accent?: boolean; danger?: boolean }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 14 }}>
      <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.12em' }}>{label}</div>
      <div className="display mono" style={{ fontSize: 22, fontWeight: 600, marginTop: 6, color: danger ? 'var(--danger)' : accent ? 'var(--accent)' : 'var(--text)', letterSpacing: '-0.02em' }}>{value}</div>
    </div>
  );
}

function EditModal({ open, user, onClose, onSaved }: { open: boolean; user: PublicUser; onClose: () => void; onSaved: () => void }) {
  const [role, setRole] = useState<PublicUser['role']>(user.role);
  const [banned, setBanned] = useState<boolean>(!!user.banned);
  React.useEffect(() => { setRole(user.role); setBanned(!!user.banned); }, [user]);
  const save = useMutation({
    mutationFn: () => api(`/api/admin/users/${user.id}`, { method: 'PATCH', body: { role, banned } }),
    onSuccess: () => { onSaved(); onClose(); },
  });
  return (
    <Modal open={open} onClose={onClose} title={`Editar ${user.email}`} footer={
      <>
        <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
        <Btn onClick={() => save.mutate()} disabled={save.isPending}>Guardar</Btn>
      </>
    }>
      <div style={{ display: 'grid', gap: 12 }}>
        <Select<PublicUser['role']>
          label="Rol"
          value={role}
          onChange={setRole}
          options={[
            { value: 'client', label: 'Cliente' },
            { value: 'operator', label: 'Operator' },
            { value: 'admin', label: 'Admin' },
          ]}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={banned} onChange={(e) => setBanned(e.target.checked)} />
          <span style={{ fontSize: 13 }}>Banear cuenta</span>
        </label>
      </div>
    </Modal>
  );
}

function AdjustModal({ open, user, onClose, onSaved }: { open: boolean; user: PublicUser; onClose: () => void; onSaved: () => void }) {
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () => api('/api/admin/wallet/adjust', { body: { userId: user.id, amount, reason } }),
    onSuccess: () => { onSaved(); onClose(); setAmount(0); setReason(''); },
  });
  return (
    <Modal open={open} onClose={onClose} title={`Ajustar saldo · ${user.email}`} footer={
      <>
        <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
        <Btn onClick={() => save.mutate()} disabled={save.isPending || amount === 0 || reason.length < 10}>Aplicar</Btn>
      </>
    }>
      <div style={{ display: 'grid', gap: 12 }}>
        <Field label="Monto (signed)" value={amount} onChange={(v) => setAmount(parseFloat(v) || 0)} type="number" step="0.01" />
        <Field label="Razón (mín. 10 chars)" value={reason} onChange={setReason} placeholder="Reembolso por error de cobro, compensación, etc." />
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
          Saldo actual: <span className="mono">${user.walletBalance.toFixed(2)}</span> → resultante: <span className="mono">${(user.walletBalance + amount).toFixed(2)}</span>
        </div>
      </div>
    </Modal>
  );
}
